import dayjs from "dayjs";
import CircleLine from "./circle-line";
import { cn } from "@/utils/theme";
import { Avatar, AvatarFallback } from "@elaniintc/goes-design-system";
import { useIntersectionObserver } from "usehooks-ts";
import { useEffect } from "react";
import type { LogsProps } from "./logs.types";
import { Description, ImpersonationBanner } from "./logs.description";
import {
  RegistryAssociateAddedBanner,
  RegistryLinkedUsers,
  BannerChanges,
} from "./logs.banner";

export default function Logs({
  title,
  items,
  emptyText,
  isLoading = false,
  hasNextPage = false,
  fetchNextPage,
  entity,
}: Readonly<LogsProps>) {
  const formatDate = (date: Date) => {
    return dayjs(date).format("MMM D, YYYY | HH:mm");
  };

  const [ref, isIntercepting, entry] = useIntersectionObserver({});

  useEffect(() => {
    if (isIntercepting && !isLoading && entry?.isIntersecting && hasNextPage) {
      fetchNextPage?.();
    }
  }, [isIntercepting, hasNextPage, entry, isLoading, fetchNextPage]);

  return (
    <div className="flex flex-col w-full h-full leading-7 text-body font-normal text-base">
      {items.length > 0 && <h2 className="mb-6">{title}</h2>}
      {/* Timeline Container */}
      {items.length !== 0 && (
        <div className="relative">
          {items.map((log, index) => {
            const isLast = index === items.length - 1;
            let circleOption: "init" | "middle" | "end" = "middle";
            if (isLast) {
              circleOption = "end";
            }
            return (
              <div
                key={`log-${log.id}-${index}`}
                className={cn("flex gap-3 -mt-2", !isLast && "mb-0")}
              >
                <CircleLine
                  option={index === 0 ? "init" : circleOption}
                  showLine={!isLast}
                />
                <div className={cn("flex-1", !isLast && "pb-6")}>
                  {/* Description */}
                  <Description log={log} entity={entity} />
                  <ImpersonationBanner log={log} />
                  {/* Details */}
                  {log.subject_type === "expedient_base_registry" && (
                    <RegistryAssociateAddedBanner log={log} />
                  )}
                  {log.subject_type === "expedient_base_registry" &&
                    log?.properties?.operation && (
                      <RegistryLinkedUsers log={log} />
                    )}
                  {log.properties?.attributes && log.properties?.old && (
                    <BannerChanges log={log} />
                  )}
                  {/* Author */}
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm text-body">Por</span>
                    <Avatar className="shrink-0" size="xs">
                      <AvatarFallback color="action">
                        {log.author.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-action">{log.author}</span>
                  </div>

                  {/* Date */}
                  <div className="text-sm text-body-quiet capitalize">
                    {formatDate(log.date)}
                  </div>
                </div>
              </div>
            );
          })}
          {hasNextPage && (
            <div
              className="w-full flex items-center justify-center h-1"
              ref={ref}
            />
          )}
        </div>
      )}
      {items.length === 0 && (
        <div className="flex-1 text-center text-body-quiet flex items-center justify-center ">
          {emptyText || "No hay actividades recientes"}
        </div>
      )}
    </div>
  );
}
